import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useStyles } from './SectorIndexPage.style'
import { useParams } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Typography,
  Tooltip,
  Box
} from '@material-ui/core'
import {
  Add,
  MonetizationOn,
  ArrowBack,
  LocalActivity
} from '@material-ui/icons'
import {
  loadLayout,
  loadSectorsPaged,
  deleteSector,
  reset
} from '~/state/modules/planimetry'
import { loadEvent } from '~/state/modules/events'
import {
  PaperContainer,
  TableGrid,
  createColumn,
  createCustomColumn,
  TableGridOrder,
  DeleteDialog
} from '~/views/shared'
import { ROLES } from 'config/constants'
import history from '~/config/history'
import { ROUTES } from '~/views/routes'
import { AssignTicketDialog } from '../AssignTicketDialog/AssignTicketDialog'
import { showSnackbarError } from 'views/utils/snackbar'
import { cleanHexColor } from 'views/utils/functions'
import { ImageDialog } from './ImageDialog'
import { SectorActionMenu } from '../SectorActionMenu'

const makeColumns = (
  setSelectedSector,
  setShowDeleteDialog,
  setOpenTicketsDialog,
  eventId,
  isEnabled,
  layoutId,
  isAdminOrProducer,
  classes
) => [
  createColumn('Name', 'Nombre', 'left', (value, row) => (
    <div className={classes.containerName}>
      {!!cleanHexColor(row?.Color) && (
        <div
          className={classes.colorTicket}
          style={{ backgroundColor: row.Color }}
        />
      )}
      <Typography variant='body2'>{row.Name}</Typography>
    </div>
  )),
  createCustomColumn(
    (c) => (
      <Typography variant='body2'>
        {c?.Row && c?.Column ? c.Row * c.Column - c.emptys : '0'}
      </Typography>
    ),
    'Cant. Max de Asientos'
  ),
  createColumn('solds', 'Asientos Vendidos'),
  createColumn('availables', 'Asientos Disponibles'),
  isAdminOrProducer &&
    createCustomColumn(
      (c) => (
        <Button
          disabled={!c.TicketsName?.length || !isEnabled}
          onClick={() => {
            history.push(
              ROUTES.ADMIN.EVENTS.SECTORS.SELL_SEATS.replace(
                ':eventId',
                eventId
              )
                .replace(':layoutId', layoutId)
                .replace(':sectorId', c.Id)
            )
          }}>
          <MonetizationOn />
        </Button>
      ),
      'Vender Planimetría'
    ),

  createCustomColumn((c) => {
    const ticketName =
      c.TicketsName && c.TicketsName.length > 0 ? c.TicketsName[0] : '-'

    return <Typography variant='body2'>{ticketName}</Typography>
  }, 'Ticket Asignado'),
  createCustomColumn(
    (c) => (
      <Tooltip
        title={
          c.Capacity === 0 ? (
            <span style={{ fontSize: 14 }}>
              Debe guardar la distribución de asientos para poder asignar
              ticket.
            </span>
          ) : (
            ''
          )
        }
        placement='top'>
        <span>
          <Button
            disabled={c.Capacity === 0}
            onClick={() => {
              setSelectedSector(c)
              setOpenTicketsDialog(true)
            }}>
            <LocalActivity />
          </Button>
        </span>
      </Tooltip>
    ),
    'Asignar Ticket'
  ),
  createCustomColumn(
    (c) => (
      <SectorActionMenu
        sector={{ ...c, EventId: eventId, LayoutId: layoutId }}
        onDeleteSector={(sector) => {
          setSelectedSector(sector)
          setShowDeleteDialog(true)
        }}
      />
    ),
    'Acciones'
  )
]

export const SectorIndexPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { eventId, layoutId } = useParams()

  const { event } = useSelector((state) => state.events)
  const isEnabled = event?.IsEnabled
  const {
    pagedSectors,
    pendingSectors,
    reqParams,
    pendingDeleteLayout,
    layout
  } = useSelector((state) => state.planimetry)
  const { user } = useSelector((state) => state.login)
  const rolesArray = user?.roles?.split(',') || []
  const isAdminOrProducer =
    rolesArray?.includes(ROLES.ADMIN) || rolesArray?.includes(ROLES.PRODUCER)
  const [selectedSector, setSelectedSector] = useState(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [openTicketsDialog, setOpenTicketsDialog] = useState(false)
  const [open, setOpen] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)

  useEffect(() => {
    async function fetchData() {
      await dispatch(loadEvent(eventId))
      await dispatch(loadLayout(layoutId))
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, eventId, layoutId])

  const handleClose = async () => {
    await dispatch(reset())
    dispatch(
      loadSectorsPaged(layoutId, {
        searchParams: '',
        pageNumber: 0,
        orderBy: 'Name',
        order: TableGridOrder.ASC
      })
    )
    dispatch(loadLayout(layoutId))
  }

  const handleDelete = async () => {
    try {
      await dispatch(deleteSector(selectedSector?.Id))
      setShowDeleteDialog(false)
      setOpen(true)
    } catch (error) {
      showSnackbarError(error)
    }
  }

  const handleBack = () => {
    history.push(ROUTES.ADMIN.EVENTS.INDEX)
  }

  const handleTicketsRedirect = () => {
    history.push(ROUTES.ADMIN.EVENTS.TICKETS.INDEX.replace(':eventId', eventId))
  }

  const columns = makeColumns(
    setSelectedSector,
    setShowDeleteDialog,
    setOpenTicketsDialog,
    eventId,
    isEnabled,
    layoutId,
    isAdminOrProducer,
    classes
  )

  return (
    <>
      {event && (
        <Typography variant='h6' className={classes.eventTitle}>
          {`${event?.Title} (${event?.Id})`}
        </Typography>
      )}

      <PaperContainer
        title={
          <div className={classes.titleContainer}>
            <Typography className={classes.title}>
              {`Listado de Sectores de "${layout?.Name ?? '-'}"`}
            </Typography>
            <div className={classes.containerButtons}>
              {layout?.ImageCdnUrl && (
                <Tooltip title='Ver imagen de la planimetría'>
                  <Box
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setOpenDialog(true)
                    }}>
                    <img
                      src={layout.ImageCdnUrl}
                      alt='plany'
                      width={90}
                      height={70}
                      className={classes.imageLayout}
                    />
                  </Box>
                </Tooltip>
              )}
              <Button
                onClick={handleTicketsRedirect}
                color='primary'
                variant='outlined'
                style={{ textTransform: 'none' }}
                startIcon={<LocalActivity />}>
                Ir a tickets del evento
              </Button>
              <Button
                onClick={handleBack}
                color='primary'
                startIcon={<ArrowBack />}>
                Volver a Eventos
              </Button>
            </div>
          </div>
        }>
        <TableGrid
          title={''}
          rowClasses={(x) => x?.IsEnabled && `${classes.conditionalRow}`}
          columns={columns}
          loadItemsPage={(x) => {
            dispatch(
              loadSectorsPaged(layoutId, {
                searchParams: '',
                pageNumber: 0,
                orderBy: x.orderBy,
                order: x.order
              })
            )
          }}
          isLoading={pendingSectors}
          pagedList={pagedSectors}
          keyName='Id'
          order={reqParams?.order || TableGridOrder.ASC}
          orderBy={reqParams?.orderBy || 'Name'}
          useNewFormat
          hideFooter
        />

        <div className={classes.createButtonContainer}>
          <Button
            onClick={() =>
              history.push(
                ROUTES.ADMIN.EVENTS.SECTORS.CREATE.replace(
                  ':eventId',
                  eventId
                ).replace(':layoutId', layoutId)
              )
            }
            color='primary'
            variant='contained'
            startIcon={<Add />}>
            Crear
          </Button>
        </div>
      </PaperContainer>

      <DeleteDialog
        open={showDeleteDialog}
        loading={pendingDeleteLayout}
        handleDelete={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
        title={'Eliminar Sector'}
        message={'Está por eliminar el sector'}
        entityIdentifier={selectedSector?.Name}
      />

      <AssignTicketDialog
        open={openTicketsDialog}
        onClose={() => setOpenTicketsDialog(false)}
        eventId={eventId}
        layoutId={layoutId}
        sectorId={selectedSector?.Id}
        sectorName={selectedSector?.Name}
        handleClose={handleClose}
        assignedTicket={
          selectedSector?.TicketsName?.length
            ? selectedSector.TicketsName[0]
            : null
        }
      />
      <ImageDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        layout={layout}
        locationId={event?.Location?.Id || null}
        onUpdateLayout={() => dispatch(loadLayout(layoutId))}
      />
      <Dialog open={open}>
        <DialogTitle>Sector eliminado</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false)
              handleClose()
            }}
            color='primary'
            autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SectorIndexPage
